(() => {
  const SELECTOR = "[data-wg-slider]";

  class WGSlider {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector("[data-wg-viewport]");
      this.track = root.querySelector(".wg-slider__track");
      this.prevButton = root.querySelector("[data-wg-prev]");
      this.nextButton = root.querySelector("[data-wg-next]");
      this.dotsContainer = root.querySelector("[data-wg-dots]");
      this.didDrag = false;

      if (!this.track) return;

      this.isAnimating = false;
      this.position = 0;
      this.direction = null;
      this.originalSlides = Array.from(this.track.children);
      this.originalCount = this.originalSlides.length;

      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.onResize = this.debounce(() => this.handleViewportChange(), 100);

      this.sliderWasEnabled = null;
      this.lastIsDesktop = window.matchMedia("(min-width: 750px)").matches;

      if (this.originalCount <= 1) {
        this.root.classList.add("is-inactive");
        return;
      }

      this.bindEvents();
      this.refreshMode();

      window.addEventListener("resize", this.onResize);
    }

    bindEvents() {
      this.nextButton?.addEventListener("click", () => {
        this.next();
      });

      this.prevButton?.addEventListener("click", () => {
        this.prev();
      });

      this.drag = {
        isPointerDown: false,
        isDragging: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        deltaX: 0,
        deltaY: 0,
      };

      this.viewport.addEventListener(
        "touchstart",
        (event) => this.onTouchStart(event),
        { passive: true },
      );

      this.viewport.addEventListener(
        "touchmove",
        (event) => this.onTouchMove(event),
        { passive: false },
      );

      this.viewport.addEventListener(
        "touchend",
        (event) => this.onTouchEnd(event),
        { passive: true },
      );

      this.viewport.addEventListener(
        "touchcancel",
        () => this.onTouchCancel(),
        { passive: true },
      );

      this.viewport.addEventListener(
        "click",
        (event) => {
          if (this.didDrag) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true,
      );
    }

    isSliderEnabled() {
      const isDesktop = window.matchMedia("(min-width: 750px)").matches;

      if (this.root.classList.contains("wg-slider-block--show-show_all")) {
        return true;
      }

      if (this.root.classList.contains("wg-slider-block--show-show_mobile")) {
        return !isDesktop;
      }

      if (this.root.classList.contains("wg-slider-block--show-show_desktop")) {
        return isDesktop;
      }

      return true;
    }

    refreshMode() {
      const shouldEnable = this.isSliderEnabled();

      if (shouldEnable) {
        this.enableSliderMode();
      } else {
        this.disableSliderMode();
      }

      this.sliderWasEnabled = shouldEnable;
      this.lastIsDesktop = window.matchMedia("(min-width: 750px)").matches;
    }

    handleViewportChange() {
      const isDesktop = window.matchMedia("(min-width: 750px)").matches;
      const sliderEnabled = this.isSliderEnabled();

      const breakpointChanged = isDesktop !== this.lastIsDesktop;
      const modeChanged = sliderEnabled !== this.sliderWasEnabled;

      if (!breakpointChanged && !modeChanged) {
        return;
      }

      this.lastIsDesktop = isDesktop;
      this.refreshMode();
    }

    enableSliderMode() {
      this.root.classList.add("is-slider-active");
      this.rebuildSlider();
    }

    disableSliderMode() {
      this.root.classList.remove("is-slider-active");
      this.root.classList.remove("is-inactive");

      this.isAnimating = false;
      this.position = 0;
      this.direction = null;

      this.setTransition(false);
      this.setTranslate(0);

      this.track.innerHTML = "";
      this.originalSlides.forEach((slide) => {
        this.track.appendChild(slide);
      });

      if (this.dotsContainer) {
        this.dotsContainer.innerHTML = "";
      }
    }

    getGap() {
      const styles = window.getComputedStyle(this.track);
      return parseFloat(styles.gap || 0);
    }

    getStepSize() {
      const firstSlide = this.track.children[0];
      if (!firstSlide) return 0;

      const slideWidth = firstSlide.getBoundingClientRect().width;
      return slideWidth + this.getGap();
    }

    getBaseOffset() {
      const viewportWidth = this.viewport.clientWidth;
      const firstSlide = this.track.children[0];
      if (!firstSlide) return 0;

      const slideWidth = firstSlide.getBoundingClientRect().width;
      const step = slideWidth + this.getGap();

      return -step + (viewportWidth - slideWidth) / 2;
    }

    setTransition(enabled) {
      if (enabled) {
        this.track.classList.remove("is-no-transition");
      } else {
        this.track.classList.add("is-no-transition");
      }
    }

    setTranslate(px) {
      this.track.style.transform = `translate3d(${px}px, 0, 0)`;
    }

    updateDots() {
      if (!this.dotsContainer) return;

      const dots = Array.from(this.dotsContainer.children);

      dots.forEach((dot, index) => {
        const isActive = index === this.position;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });
    }

    buildDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = "";

      for (let i = 0; i < this.originalCount; i++) {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "wg-slider__dot";
        dot.setAttribute("aria-label", `Go to slide ${i + 1}`);

        dot.addEventListener("click", () => {
          this.goToPosition(i);
        });

        this.dotsContainer.appendChild(dot);
      }

      this.updateDots();
    }

    next() {
      if (!this.root.classList.contains("is-slider-active")) return;
      if (this.isAnimating || this.originalCount <= 1) return;
      this.commitDragNext();
    }

    prev() {
      if (!this.root.classList.contains("is-slider-active")) return;
      if (this.isAnimating || this.originalCount <= 1) return;
      this.commitDragPrev();
    }

    onTransitionEnd() {
      if (!this.root.classList.contains("is-slider-active")) return;

      if (this.direction === "next") {
        this.setTransition(false);
        // Remove old prev buffer, append to end
        this.track.appendChild(this.track.firstElementChild);
        // Restore centered base offset
        this.setTranslate(this.getBaseOffset());
        this.position = (this.position + 1) % this.originalCount;
      } else if (this.direction === "prev") {
        this.setTransition(false);
        // Prepend new prev buffer from end
        this.track.insertBefore(
          this.track.lastElementChild,
          this.track.firstElementChild,
        );
        this.setTranslate(this.getBaseOffset());
        this.position =
          (this.position - 1 + this.originalCount) % this.originalCount;
      }

      this.forceReflow();
      this.setTransition(true);

      this.isAnimating = false;
      this.direction = null;
      this.updateDots();
    }

    goToPosition(targetPosition) {
      if (!this.root.classList.contains("is-slider-active")) return;
      if (this.isAnimating || targetPosition === this.position) return;

      this.setTransition(false);

      // Rebuild DOM order for target position
      this.track.innerHTML = "";
      this.originalSlides.forEach((slide) => {
        this.track.appendChild(slide);
      });

      for (let i = 0; i < targetPosition; i += 1) {
        this.track.appendChild(this.track.firstElementChild);
      }

      // Prepend prev buffer
      this.track.insertBefore(
        this.track.lastElementChild,
        this.track.firstElementChild,
      );

      this.setTranslate(this.getBaseOffset());
      this.position = targetPosition;
      this.updateDots();

      requestAnimationFrame(() => {
        this.setTransition(true);
      });
    }

    rotateToPosition(direction, steps) {
      if (steps === 0) return;

      const runStep = () => {
        if (steps <= 0) return;

        if (direction === "next") {
          this.next();
        } else {
          this.prev();
        }

        const waitUntilDone = () => {
          if (!this.isAnimating) {
            steps -= 1;

            if (steps > 0) {
              runStep();
            }
          } else {
            requestAnimationFrame(waitUntilDone);
          }
        };

        waitUntilDone();
      };

      runStep();
    }

    rebuildSlider() {
      const preservedPosition = Math.min(
        this.position,
        Math.max(0, this.originalCount - 1),
      );

      this.track.innerHTML = "";
      this.originalSlides.forEach((slide) => {
        this.track.appendChild(slide);
      });

      for (let i = 0; i < preservedPosition; i += 1) {
        this.track.appendChild(this.track.firstElementChild);
      }

      this.position = preservedPosition;
      this.setTransition(false);

      if (this.originalCount <= 1) {
        this.setTranslate(0);
        this.root.classList.add("is-inactive");
        return;
      }

      // Prepend last slide as prev buffer so both sides always peek
      this.track.insertBefore(
        this.track.lastElementChild,
        this.track.firstElementChild,
      );

      this.root.classList.remove("is-inactive");
      this.buildDots();

      requestAnimationFrame(() => {
        this.setTranslate(this.getBaseOffset());
        requestAnimationFrame(() => {
          this.setTransition(true);
          this.updateDots();
        });
      });
    }

    forceReflow() {
      void this.track.offsetHeight;
    }

    debounce(fn, wait) {
      let timeout;

      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    onTouchStart(event) {
      if (!this.root.classList.contains("is-slider-active")) return;
      if (this.isAnimating) return;

      const touch = event.touches[0];

      this.drag.isPointerDown = true;
      this.drag.isDragging = false;
      this.drag.startX = touch.clientX;
      this.drag.startY = touch.clientY;
      this.drag.currentX = touch.clientX;
      this.drag.deltaX = 0;
      this.drag.deltaY = 0;

      this.root.classList.add("is-touching");
    }

    onTouchMove(event) {
      if (!this.root.classList.contains("is-slider-active")) return;
      if (!this.drag.isPointerDown) return;
      if (this.isAnimating) return;

      const touch = event.touches[0];
      this.drag.currentX = touch.clientX;
      this.drag.deltaX = touch.clientX - this.drag.startX;
      this.drag.deltaY = touch.clientY - this.drag.startY;
      this.didDrag = true;

      const absX = Math.abs(this.drag.deltaX);
      const absY = Math.abs(this.drag.deltaY);

      if (!this.drag.isDragging) {
        if (absX < 8) return;

        if (absY > absX) {
          this.onTouchCancel();
          return;
        }

        this.drag.isDragging = true;
        this.root.classList.add("is-dragging");
      }

      event.preventDefault();

      const step = this.getStepSize();
      const maxDrag = step * 0.35;
      const constrainedDelta = Math.max(
        -maxDrag,
        Math.min(maxDrag, this.drag.deltaX),
      );

      this.setTransition(false);
      this.setTranslate(this.getBaseOffset() + constrainedDelta);
    }

    onTouchEnd() {
      if (!this.root.classList.contains("is-slider-active")) return;
      if (!this.drag.isPointerDown) return;

      const deltaX = this.drag.deltaX;
      const wasDragging = this.drag.isDragging;
      const threshold = Math.min(80, this.getStepSize() * 0.18);

      this.drag.isPointerDown = false;
      this.drag.isDragging = false;
      this.root.classList.remove("is-touching");
      this.root.classList.remove("is-dragging");
      setTimeout(() => {
        this.didDrag = false;
      }, 50);

      if (!wasDragging) {
        this.setTransition(true);
        this.setTranslate(this.getBaseOffset());
        return;
      }

      if (Math.abs(deltaX) >= threshold) {
        if (deltaX < 0) {
          this.commitDragNext();
        } else {
          this.commitDragPrev();
        }
      } else {
        this.setTransition(true);
        this.setTranslate(this.getBaseOffset());
      }
    }

    onTouchCancel() {
      this.drag.isPointerDown = false;
      this.drag.isDragging = false;
      this.drag.deltaX = 0;
      this.drag.deltaY = 0;
      this.root.classList.remove("is-touching");
      this.root.classList.remove("is-dragging");

      this.setTransition(true);
      this.setTranslate(this.getBaseOffset());
    }

    commitDragNext() {
      if (this.isAnimating) return;

      this.isAnimating = true;
      this.direction = "next";

      const step = this.getStepSize();

      this.track.removeEventListener("transitionend", this.onTransitionEnd);
      this.track.addEventListener("transitionend", this.onTransitionEnd, {
        once: true,
      });

      this.setTransition(true);
      this.setTranslate(this.getBaseOffset() - step);
    }

    commitDragPrev() {
      if (this.isAnimating) return;

      this.isAnimating = true;
      this.direction = "prev";

      const step = this.getStepSize();

      this.track.removeEventListener("transitionend", this.onTransitionEnd);
      this.track.addEventListener("transitionend", this.onTransitionEnd, {
        once: true,
      });

      this.setTransition(true);
      this.setTranslate(this.getBaseOffset() + step);
    }
  }

  const init = () => {
    document.querySelectorAll(SELECTOR).forEach((slider) => {
      if (slider.dataset.wgSliderReady === "true") return;
      slider.dataset.wgSliderReady = "true";
      new WGSlider(slider);
    });
  };

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:reorder", init);
})();